import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Send, Sparkles, ShieldCheck, Loader2, Volume2, VolumeX } from "lucide-react";
import { API, ASSETS, GAME_STYLES, getSessionId, apiFetch } from "@/lib/gp";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import ShowcaseSidebar from "@/components/ShowcaseSidebar";
import MessageContent from "@/components/MessageContent";
import YorickGauntlet from "@/components/YorickGauntlet";
import {
  DEFAULT_VOICE_VOLUME,
  MAX_VOICE_VOLUME,
  MIN_VOICE_VOLUME,
  YORICK_GREETING_SPEECH,
  YORICK_VOICE_SAMPLE,
  greetingAlreadyPlayed,
  loadVoicePrefs,
  markGreetingPlayed,
  saveVoicePrefs,
  speakYorick,
  stopYorickVoice,
  unlockYorickVoice,
  voiceSupported,
} from "@/lib/yorickVoice";

const INTRO = {
  role: "assistant",
  content:
    "Alas, a challenger approaches. I'm **Yorick** — your strategy coach across Balatro, Slay the Spire, Hades and beyond.\n\nPick a game above or just ask me anything. So… **how can I help you win?**",
  intro: true,
};

export default function Chat() {
  const [featured, setFeatured] = useState([]);
  const [showcase, setShowcase] = useState([]);
  const [gamesError, setGamesError] = useState("");
  const [activeGame, setActiveGame] = useState(null);
  const [messages, setMessages] = useState([INTRO]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [detected, setDetected] = useState(null);
  const [voiceMuted, setVoiceMuted] = useState(() => loadVoicePrefs().muted);
  const [voiceVolume, setVoiceVolume] = useState(() => loadVoicePrefs().volume);
  const scrollRef = useRef(null);
  const challengeRef = useRef(null);
  const spokenRef = useRef("");
  const allowSpeakRef = useRef(false);
  const { user } = useAuth();
  const sessionId = user === null ? null : getSessionId();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (params.get("challenge") !== "1") return;
    const timer = window.setTimeout(() => challengeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
    return () => window.clearTimeout(timer);
  }, [params]);

  useEffect(() => {
    fetch(`${API}/games`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`the games API answered ${r.status}`))))
      .then((d) => {
        setFeatured(d.featured || []);
        setShowcase(d.showcase || []);
        const g = params.get("game");
        if (g && [...(d.featured || []), ...(d.showcase || [])].some((x) => x.id === g)) setActiveGame(g);
      })
      .catch((e) => setGamesError(e?.message || "the games API could not be reached"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    apiFetch(`/chat/${sessionId}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.detail || "Could not load this conversation.");
        return data;
      })
      .then((d) => {
        if (d.messages?.length) setMessages(d.messages.map((m) => ({ role: m.role, content: m.content })));
        else setMessages([INTRO]);
      })
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    saveVoicePrefs({ muted: voiceMuted, volume: voiceVolume });
  }, [voiceMuted, voiceVolume]);

  useEffect(() => () => stopYorickVoice(), []);

  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return undefined;
    const warm = () => synth.getVoices();
    warm();
    synth.addEventListener("voiceschanged", warm);
    return () => synth.removeEventListener("voiceschanged", warm);
  }, []);

  useEffect(() => {
    if (loading || !allowSpeakRef.current) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant" || last.intro || last.streaming || !last.content) return;
    if (last.content === spokenRef.current) return;
    spokenRef.current = last.content;
    if (voiceMuted) return;
    speakYorick(last.content, { volume: voiceVolume, muted: false, style: "reply" });
  }, [loading, messages, voiceMuted, voiceVolume]);

  useEffect(() => {
    if (voiceMuted || !voiceSupported()) return undefined;
    if (greetingAlreadyPlayed()) return undefined;
    if (user === null) return undefined;
    const onlyIntro = messages.length === 1 && messages[0]?.intro;
    if (!onlyIntro) return undefined;

    let cancelled = false;
    const playGreeting = () => {
      if (cancelled || voiceMuted || greetingAlreadyPlayed()) return;
      if (window.speechSynthesis?.speaking) return;
      unlockYorickVoice();
      speakYorick(YORICK_GREETING_SPEECH, {
        volume: voiceVolume,
        muted: false,
        style: "greeting",
        onStart: markGreetingPlayed,
      });
    };

    const delay = window.setTimeout(playGreeting, 1100);
    window.addEventListener("pointerdown", playGreeting);
    window.addEventListener("keydown", playGreeting);
    return () => {
      cancelled = true;
      clearTimeout(delay);
      window.removeEventListener("pointerdown", playGreeting);
      window.removeEventListener("keydown", playGreeting);
    };
  }, [messages, voiceMuted, voiceVolume, user]);

  const activeSuggestions =
    suggestions.length > 0
      ? suggestions
      : ([...featured, ...showcase].find((g) => g.id === activeGame)?.suggestions ||
          featured[0]?.suggestions ||
          []);

  const send = useCallback(
    async (text) => {
      const msg = (text ?? input).trim();
      if (!msg || loading || !sessionId) return;
      allowSpeakRef.current = true;
      unlockYorickVoice();
      stopYorickVoice();
      setInput("");
      setSuggestions([]);
      setMessages((m) => [...m.filter((x) => !x.intro), { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);
      setLoading(true);

      try {
        const res = await apiFetch("/chat/stream", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId, message: msg, game_id: activeGame }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          const detail = String(data.detail || "");
          if (res.status === 404 || /conversation not found/i.test(detail)) {
            throw new Error("Yorick couldn't open this chat. Refresh the page and try that again.");
          }
          throw new Error(detail || "Yorick could not answer right now.");
        }
        if (!res.body) throw new Error("Yorick's response stream is unavailable.");
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split("\n\n");
          buffer = chunks.pop() || "";
          for (const chunk of chunks) {
            const lines = chunk.split("\n");
            let event = "message";
            let data = "";
            for (const line of lines) {
              if (line.startsWith("event:")) event = line.slice(6).trim();
              else if (line.startsWith("data:")) data += line.slice(5).trim();
            }
            if (!data) continue;
            let payload;
            try { payload = JSON.parse(data); } catch { continue; }

            if (event === "meta") {
              setDetected(payload.detected_game || null);
            } else if (event === "done") {
              setSuggestions(payload.suggestions || []);
            } else if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, content: last.content + payload.delta };
                }
                return copy;
              });
            }
          }
        }
      } catch (e) {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant" && !last.content) {
            copy[copy.length - 1] = {
              ...last,
              content: e?.message || "Yorick lost connection to the table. Try again in a moment.",
            };
          }
          return copy;
        });
      } finally {
        setMessages((m) => m.map((x) => ({ ...x, streaming: false })));
        setLoading(false);
      }
    },
    [input, loading, activeGame, sessionId]
  );

  const askAboutShowcase = (g) => {
    navigate(`/game/${g.id}`);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-8 py-6 md:py-10">
      <div className="flex gap-8">
        <div className="flex-1 min-w-0">
          {/* Game tabs */}
          {gamesError && (
            <p className="text-xs text-amber-300/90 mb-2" data-testid="game-tabs-error">
              Not working yet: the game tabs could not be loaded from the API — {gamesError}. Yorick still answers if you type a question.
            </p>
          )}
          <div className="flex items-end gap-2 overflow-x-auto pb-1 gp-scroll" data-testid="game-tabs">
            {[...featured, ...showcase.filter((g) => g.id === activeGame && !featured.some((f) => f.id === g.id))].map((g) => {
              const st = GAME_STYLES[g.id] || {};
              const Icon = st.icon;
              const active = activeGame === g.id;
              return (
                <button
                  key={g.id}
                  data-testid={`game-tab-${g.id}`}
                  onClick={() => setActiveGame(active ? null : g.id)}
                  className={`shrink-0 flex items-center gap-2 rounded-t-xl px-4 py-3 border-b-2 transition-all ${
                    active
                      ? "bg-gp-surface border-gp-gold -translate-y-0.5"
                      : "bg-white/5 border-transparent hover:bg-white/10 text-white/70"
                  }`}
                  style={active ? { boxShadow: `0 6px 22px ${st.from}33` } : {}}
                >
                  {Icon && <Icon size={16} style={{ color: active ? st.from : undefined }} />}
                  <span className={`text-sm font-semibold ${active ? "text-white" : ""}`}>{g.name}</span>
                </button>
              );
            })}
          </div>

          {/* Cinematic Yorick conversation */}
          <div className="overflow-hidden rounded-3xl border border-gp-blue/30 bg-[#070b12] shadow-[0_24px_80px_rgba(0,0,0,0.55)]" data-testid="chat-window">
            <div
              className="relative h-[42vh] min-h-[320px] max-h-[520px] bg-cover bg-[position:center_30%]"
              style={{
                backgroundImage: `url(${ASSETS.chatPortrait})`,
                backgroundSize: "cover",
                backgroundPosition: "center 30%",
                backgroundRepeat: "no-repeat",
              }}
              role="img"
              aria-label="Yorick, GamePlat's electric-eyed strategy coach"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-[#070b12] via-transparent to-black/45" />
              <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-4 p-5 md:p-6">
                <div className="rounded-2xl border border-white/10 bg-black/45 px-4 py-3 backdrop-blur-md">
                  <p className="font-unbounded font-black tracking-tight text-xl">Yorick</p>
                  <p className="mt-1 text-xs text-white/65">
                    {detected ? `Strategizing for ${detected.name}` : "Your strategy coach • online"}
                  </p>
                </div>
                <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-gp-green bg-black/45 px-3 py-1.5 rounded-full ring-1 ring-gp-green/30 backdrop-blur-md">
                  <ShieldCheck size={13} /> Fair-play only
                </span>
              </div>
              <div className="absolute inset-x-0 bottom-0 px-5 pb-5 md:px-7 md:pb-7">
                <p className="font-vt323 text-xs uppercase tracking-[0.24em] text-gp-gold">The strategist awaits</p>
                <p className="mt-1 max-w-2xl text-sm text-white/70">Choose a game or speak freely. Yorick answers out loud at a moderate level — not at your device’s full volume. Adjust here or on your phone, tablet, or computer.</p>
              </div>
            </div>

            <div className="border-t border-gp-blue/25 bg-gradient-to-b from-[#0b1422] to-[#070b12]">
              <div className="border-b border-white/10 px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <p className="font-vt323 text-xs uppercase tracking-[0.24em] text-gp-gold">Conversation</p>
                {voiceSupported() && (
                  <div className="flex items-center gap-2.5 min-w-0" data-testid="yorick-voice-controls" title="Yorick speaks below your device volume. Use this slider or your phone, tablet, or computer volume.">
                    <button
                      type="button"
                      data-testid="yorick-voice-mute"
                      aria-label={voiceMuted ? "Unmute Yorick" : "Mute Yorick"}
                      onClick={() => {
                        unlockYorickVoice();
                        setVoiceMuted((m) => {
                          if (!m) stopYorickVoice();
                          return !m;
                        });
                      }}
                      className="shrink-0 p-1.5 rounded-full hover:bg-white/10 text-white/70"
                    >
                      {voiceMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                    </button>
                    <Slider
                      data-testid="yorick-voice-volume"
                      min={Math.round(MIN_VOICE_VOLUME * 100)}
                      max={Math.round(MAX_VOICE_VOLUME * 100)}
                      step={1}
                      value={[Math.round(voiceVolume * 100)]}
                      disabled={voiceMuted}
                      onValueChange={(vals) => {
                        const next = (vals?.[0] ?? DEFAULT_VOICE_VOLUME * 100) / 100;
                        setVoiceVolume(next);
                        if (next > 0 && voiceMuted) setVoiceMuted(false);
                      }}
                      aria-label="Yorick voice volume"
                      className="w-28 sm:w-36"
                    />
                    <span className="text-[11px] text-white/45 tabular-nums w-8">{Math.round(voiceVolume * 100)}%</span>
                    <button
                      type="button"
                      data-testid="yorick-voice-sample"
                      onClick={() => {
                        unlockYorickVoice();
                        if (voiceMuted) setVoiceMuted(false);
                        speakYorick(YORICK_VOICE_SAMPLE, {
                          volume: voiceVolume,
                          muted: false,
                          style: "greeting",
                        });
                      }}
                      className="shrink-0 rounded-full border border-gp-gold/30 bg-gp-gold/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-gp-gold hover:bg-gp-gold/20"
                    >
                      Sample
                    </button>
                  </div>
                )}
              </div>

              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="flex items-center gap-3 border-b border-white/10 p-4 md:px-6"
              >
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Speak to Yorick…"
                  aria-label="Message Yorick"
                  className="flex-1 rounded-full bg-black/40 px-5 py-3 text-[15px] outline-none ring-1 ring-white/10 transition-all placeholder:text-white/35 focus:ring-gp-blue/60"
                />
                <Button
                  type="submit"
                  data-testid="chat-send"
                  disabled={loading || !input.trim()}
                  aria-label="Send message"
                  className="h-12 w-12 rounded-full bg-gp-blue p-0 shadow-[0_0_20px_rgba(22,119,255,0.48)] hover:bg-gp-blue/90 disabled:opacity-40"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                </Button>
              </form>

              <div ref={scrollRef} className="min-h-[120px] max-h-[260px] overflow-y-auto gp-scroll px-4 py-5 md:px-6" data-testid="dialogue-transcript">
                <div className="space-y-4">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[88%] ${m.role === "user" ? "text-right" : "text-left"}`}>
                        <p className={`mb-1 font-vt323 text-[11px] uppercase tracking-[0.22em] ${m.role === "user" ? "text-gp-blue" : "text-gp-gold"}`}>
                          {m.role === "user" ? "Challenger" : "Yorick"}
                        </p>
                        <div
                          className={`rounded-2xl px-4 py-3 text-[15px] leading-relaxed ring-1 ${
                            m.role === "user"
                              ? "bg-gp-blue/15 text-white ring-gp-blue/35 rounded-br-sm"
                              : "bg-black/35 text-white/90 ring-white/10 rounded-bl-sm"
                          }`}
                          data-testid={`msg-${m.role}`}
                        >
                          {m.role === "assistant" ? (
                            <>
                              <MessageContent text={m.content} />
                              {m.streaming && !m.content && (
                                <span className="inline-flex items-center gap-2 text-white/50 text-sm">
                                  <Loader2 className="animate-spin" size={14} /> Yorick is thinking…
                                </span>
                              )}
                              {m.streaming && m.content && <span className="ml-0.5 inline-block h-4 w-2 bg-gp-blue align-middle animate-pulse-glow" />}
                              {m.content && !m.streaming && voiceSupported() && (
                                <button
                                  type="button"
                                  data-testid="yorick-replay"
                                  aria-label="Play this Yorick message"
                                  onClick={() => {
                                    unlockYorickVoice();
                                    if (voiceMuted) setVoiceMuted(false);
                                    speakYorick(m.intro ? YORICK_GREETING_SPEECH : m.content, {
                                      volume: voiceVolume,
                                      muted: false,
                                      style: m.intro ? "greeting" : "reply",
                                      onStart: m.intro ? markGreetingPlayed : undefined,
                                    });
                                  }}
                                  className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-white/40 hover:text-gp-gold"
                                >
                                  <Volume2 size={12} /> Listen
                                </button>
                              )}
                            </>
                          ) : (
                            m.content
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {activeSuggestions.length > 0 && (
                <div className="flex flex-wrap gap-2 border-t border-white/8 px-4 py-3 md:px-6" data-testid="suggestions">
                  {activeSuggestions.slice(0, 4).map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      disabled={loading}
                      className="flex items-center gap-1.5 rounded-full border border-gp-blue/25 bg-gp-blue/8 px-3 py-1.5 text-xs text-white/75 transition-colors hover:bg-gp-blue/15 disabled:opacity-40"
                    >
                      <Sparkles size={12} className="text-gp-gold" /> {s}
                    </button>
                  ))}
                </div>
              )}

              <div ref={challengeRef}>
                <YorickGauntlet embedded />
              </div>
            </div>
          </div>
        </div>

        <ShowcaseSidebar games={showcase} onAsk={askAboutShowcase} />
      </div>
    </div>
  );
}
